import dayjs from 'dayjs'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { adaptGetSupplierAppAuthorizationResponse } from 'shared/adapters/integrations'
import { getSupplierAppAuthorization } from 'shared/api/integrations'

import { SUPPLIER_APP_INTEGRATION_STATUSES } from 'retailer/constants'

/**
 * A base SupplierAppIntegration store. Just provide the integrationName and it will call
 * the backend API automatically.
 * If you need more custom behavior, you can extend from it.
 * See `AliExpressIntegrationStore` for example.
 */
class SupplierAppIntegrationStore {
  integrationName
  appConfigStore
  @observable authorization
  @observable isLoading = false
  @observable error
  @observable marketplaceNavigationRequested

  constructor (integrationName, appConfigStore) {
    makeObservable(this)
    this.integrationName = integrationName
    this.appConfigStore = appConfigStore
  }

  @computed get connectionText () {
    // Your integration with AliExpress is active
    switch (this.status) {
      case SUPPLIER_APP_INTEGRATION_STATUSES.connected:
        return `Your connection to ${this.integrationNameDisplay} will expire ${this.timeToExpire}`
      case SUPPLIER_APP_INTEGRATION_STATUSES.expired:
        return `Your connection to ${this.integrationNameDisplay} expired ${this.timeToExpire}`
      default:
        return `Connect your account to ${this.integrationNameDisplay}`
    }
  }

  @computed get status () {
    if (this.isLoading === false) {
      const { connected, expired, unconnected } = SUPPLIER_APP_INTEGRATION_STATUSES
      if (this.authorization) {
        return this.authorization.isExpired ? expired : connected
      }
      return unconnected
    }
    return undefined
  }

  @computed get isConnected () {
    return this.status && this.status === SUPPLIER_APP_INTEGRATION_STATUSES.connected
  }

  @computed get timeToExpire () {
    return dayjs(this.authorization.accessTokenExpireTime).fromNow()
  }

  @action.bound
  fetchAuthorization () {
    this.isLoading = true
    getSupplierAppAuthorization(this.integrationName)
      .then((response) => {
        const data = adaptGetSupplierAppAuthorizationResponse(response.data)
        runInAction(() => {
          this.authorization = data
        })
      })
      .catch(error => {
        runInAction(() => {
          this.error = error
        })
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false
        })
      })
  }
}

export default SupplierAppIntegrationStore
